import type { AppRole, BusinessArm } from "../types";

// Almikia business-unit names are business titles, shown in Spanish whatever
// the locale (same convention as `ROLE_LABELS` in `./roles.ts`). The field is
// still called `business_arm` in the data; only the visible word changed.
export const BUSINESS_ARM_LABELS: Record<BusinessArm, string> = {
  corretaje: "Corretaje",
  bpo: "BPO",
  ambos: "Ambos",
  direccion_general: "Dirección general",
};

type BusinessUnitChoice = { id: BusinessArm; name: string; disabled?: boolean };

const toChoice = (arm: BusinessArm, disabled = false): BusinessUnitChoice => ({
  id: arm,
  name: BUSINESS_ARM_LABELS[arm],
  ...(disabled ? { disabled: true } : {}),
});

/**
 * The business units a person can be given: Corretaje or BPO (never both),
 * plus Dirección general for the direccion role only. A stored value that is
 * no longer selectable (legacy 'ambos', or a direccion_general left on a
 * person who is no longer direccion) is kept as a disabled choice so the
 * select still renders it instead of going blank.
 */
export const getBusinessUnitChoices = (
  role: AppRole | undefined,
  currentArm: BusinessArm | null | undefined,
): BusinessUnitChoice[] => {
  const choices = [toChoice("corretaje"), toChoice("bpo")];
  if (role === "direccion") {
    choices.push(toChoice("direccion_general"));
  }
  if (currentArm && !choices.some((choice) => choice.id === currentArm)) {
    choices.push(toChoice(currentArm, true));
  }
  return choices;
};

/**
 * True when the submitted unit moves a person TO Corretaje while she still
 * has active project memberships. An already-Corretaje person (rotation
 * advisors keep memberships) is never blocked.
 */
export const isSwitchingToCorretajeWithProjects = (
  storedArm: BusinessArm | null | undefined,
  submittedArm: BusinessArm | null | undefined,
  activeMembershipCount: number,
): boolean =>
  submittedArm === "corretaje" &&
  storedArm !== "corretaje" &&
  activeMembershipCount > 0;
